/**
 * Utility to manage the last visited route for persistent navigation
 */

import { withVesselParams } from './vesselHandover';
import { reportError } from '@/observability/clientLogger';

const LAST_VISITED_ROUTE_KEY = 'lastVisitedRoute';

/**
 * Delete the cookie earlier builds wrote. Only needed until existing browsers have
 * navigated once; harmless when there is nothing to delete.
 */
function expireLegacyCookie(): void {
  try {
    if (document.cookie.includes(`${LAST_VISITED_ROUTE_KEY}=`)) {
      document.cookie = `${LAST_VISITED_ROUTE_KEY}=; path=/; max-age=0`;
    }
  } catch {
    // Cookies unavailable (partitioned or disabled) — nothing to clean up.
  }
}

/**
 * Save the current route to localStorage.
 * Also emits a postMessage to the parent window so the chat interface can
 * capture the route even when Safari partitions the iframe's localStorage.
 * @param route - The route path to save (e.g., '/class', '/eta-monitoring')
 */
export function saveLastVisitedRoute(route: string): void {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(LAST_VISITED_ROUTE_KEY, route);

      // This was also written to a cookie "for server-side access", but nothing ever
      // read it there — no getServerSideProps, middleware or API route — so it only
      // added weight to every request. Expire any copy a previous build left behind;
      // those were set with a 30-day max-age and would otherwise linger.
      expireLegacyCookie();

      // Notify the parent chat interface of the route. Safari partitions this
      // iframe's localStorage away from the top-level microapps origin, so when the
      // chat's open-in-new-tab loads the shell standalone it cannot read this value —
      // it reads its own bucket and lands on whatever that last held. The parent is
      // meant to capture the route here and re-supply it as ?lastVisitedRoute= on the
      // new-tab URL. The selected vessel is partitioned away by exactly the same rule,
      // so it travels in this route's query rather than in a channel of its own (see
      // utils/vesselHandover).
      //
      // NOTE: as of Sep 2026 the deployed SIYA SPA does neither — its bundle has no
      // listener for this message, and its panel header is a plain <a href> built from
      // the static `microapps` URL template (`?node=&authToken=` only). So this post is
      // currently received by nobody, and BOTH route and vessel restore wrong in Safari.
      // The receiving half is done here; the chat has to forward what it is sent.
      postRouteToParent(route);
    } catch (error) {
      reportError(error, { event_detail: 'last_route_save_failed' });
    }
  }
}

/**
 * Post a route to the parent chat: first bare, then again with the vessel in its query.
 *
 * Two messages, in that order, because the parent's validation of what it will replay
 * as ?lastVisitedRoute= is not ours to see. A parent that keeps the last route it is
 * given takes the second and hands the new tab both page AND vessel; one that only
 * accepts a bare path ignores the second and still has the first, so route restore
 * cannot regress on the way to fixing the vessel. Neither costs more than a postMessage.
 *
 * Only the parent's LAST accepted route survives to the new-tab URL, so this is also
 * how a vessel change with no navigation reaches it: vessel-bridge re-posts the
 * stored route so the vessel in the parent's copy is never a selection behind.
 */
function postRouteToParent(route: string): void {
  if (!window.parent || window.parent === window) return;
  window.parent.postMessage({ type: 'routeChange', route }, '*');

  const withVessel = withVesselParams(route);
  if (withVessel !== route) {
    window.parent.postMessage({ type: 'routeChange', route: withVessel }, '*');
  }
}

/**
 * Re-post the stored route so the parent picks up a vessel change that happened
 * without a navigation. No-op outside the chat iframe, or before any route is saved.
 */
export function repostLastVisitedRoute(): void {
  if (typeof window === 'undefined') return;
  try {
    const route = localStorage.getItem(LAST_VISITED_ROUTE_KEY);
    if (route) postRouteToParent(route);
  } catch {
    // Storage unavailable — nothing to re-post.
  }
}

/**
 * Get the last visited route from localStorage
 * @returns The last visited route or null if not found
 */
export function getLastVisitedRoute(): string | null {
  if (typeof window !== 'undefined') {
    try {
      const route = localStorage.getItem(LAST_VISITED_ROUTE_KEY);
      return route;
    } catch (error) {
      reportError(error, { event_detail: 'last_route_read_failed' });
      return null;
    }
  }
  return null;
}

/**
 * Clear the last visited route from localStorage
 */
export function clearLastVisitedRoute(): void {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(LAST_VISITED_ROUTE_KEY);
      expireLegacyCookie();
    } catch (error) {
      reportError(error, { event_detail: 'last_route_clear_failed' });
    }
  }
}
